import React from 'react';
import { token } from '@atlaskit/tokens';
import { applyTransitionIconConditions } from './calculateDefaultGlobalLoopsContainerPosition.tsx';
import { STATE_COLOURS } from './ellipsis.tsx';
import {
	getContainerHeight,
	getGlobalLoopTransitionPosition,
	CONTAINER_WIDTH,
} from './getLozengePosition.tsx';
import { GlobalLoopedTransition } from './GlobalLoopedTransition.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import MoveArrow, { ARROW_RADIUS } from './MoveArrow.tsx';
import type { Point, StatefulDiagramGlobalLoopedTransition } from './toPortInfo.tsx';
import { useDraggableDataAttributes } from './useCanvasDragDropEvents.tsx';

const OUTLINE_PADDING = 3;
const FOCUS_RING_WIDTH = 4;
const ARROW_PADDING = OUTLINE_PADDING + FOCUS_RING_WIDTH + ARROW_RADIUS;

export interface Props {
	isImmutable: boolean;
	position: Point;
	transitions: StatefulDiagramGlobalLoopedTransition[];
	isMoving: boolean;
	isTargeted: boolean;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
}

export const GlobalLoopsContainer = ({
	position,
	isMoving,
	isTargeted,
	transitions,
	isImmutable,
	onSelect,
	onHover,
}: Props): React.JSX.Element | null => {
	const dataAttributes = useDraggableDataAttributes(
		'global-loops-container',
		'global-loops-container',
	);
	const containerHeight = getContainerHeight(transitions.length);

	if (transitions.length === 0) {
		return null;
	}

	return (
		<g data-testid="accessible-workflow-diagram.global-loops-container" {...dataAttributes}>
			{isTargeted && (
				<rect
					x={position.x - OUTLINE_PADDING - FOCUS_RING_WIDTH / 4}
					y={position.y - OUTLINE_PADDING - FOCUS_RING_WIDTH / 4}
					width={CONTAINER_WIDTH + OUTLINE_PADDING * 2 + FOCUS_RING_WIDTH / 2}
					height={containerHeight + OUTLINE_PADDING * 2 + FOCUS_RING_WIDTH / 2}
					rx="3.5"
					ry="3.5"
					stroke={STATE_COLOURS.focused}
					fillOpacity="0"
					strokeWidth={FOCUS_RING_WIDTH}
				/>
			)}
			{isMoving && (
				<>
					<MoveArrow
						x={position.x + CONTAINER_WIDTH / 2}
						y={position.y - ARROW_PADDING}
						direction="up"
					/>
					<MoveArrow
						x={position.x + CONTAINER_WIDTH / 2}
						y={position.y + containerHeight + ARROW_PADDING}
						direction="down"
					/>
					<MoveArrow
						x={position.x - ARROW_PADDING}
						y={position.y + containerHeight / 2}
						direction="left"
					/>
					<MoveArrow
						x={position.x + CONTAINER_WIDTH + ARROW_PADDING}
						y={position.y + containerHeight / 2}
						direction="right"
					/>
				</>
			)}
			<rect
				x={position.x}
				y={position.y}
				width={CONTAINER_WIDTH}
				height={containerHeight}
				rx="3.5"
				fill={token('elevation.surface')}
				fillOpacity="0.8"
				stroke={token('color.border')}
				cursor={isImmutable ? undefined : 'move'}
			/>
			{transitions.map((transition, index) => (
				<GlobalLoopedTransition
					id={transition.id}
					key={transition.id}
					position={getGlobalLoopTransitionPosition(position, index)}
					label={transition.label}
					flag={transition.flag}
					isHovered={transition.isHovered}
					isImmutable={isImmutable}
					isSelected={transition.isSelected}
					isTargeted={transition.isTargeted}
					onSelect={onSelect}
					onHover={onHover}
					icons={applyTransitionIconConditions(transition.icons, undefined)}
				/>
			))}
		</g>
	);
};

export default GlobalLoopsContainer;
